<template>
  <v-dialog v-model="dialog" width="900" :persistent="true">
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title class="food-name-container">
          <span class="food-name">{{ foodName }}</span>
          <span>{{ `app.foods.portions_count`|trans }}: {{ portions }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-tabs v-model="selectedTab" slider-color="success" v-if="dialog">
          <v-tab href="#basic">
            {{ `app.foods.basic`|trans }}
          </v-tab>
          <v-tab href="#details">
            {{ `app.foods.details`|trans }}
          </v-tab>
          <v-spacer></v-spacer>
          <div class="d-flex justify-content-center align-items-center">
            <permission-button
                :permission="canBeEdited()"
                color="primary"
                :tooltip="{top: true, bottom: false, attach: false}"
                @click="edit"
                class="ml-0 mr-0"
                :no-permission-tool-tip="(isSystem && !hasPermission('food_system_edit') || isShared) ? 'app.edit' : 'app.no_access_tooltip'"
            >
              {{ 'app.edit' | trans }}
            </permission-button>
          </div>
          <v-tab-item
              :key="`basic`"
              :value="`basic`"
          >
            <v-layout align-center justify-center v-if="loading" class="loader-container">
              <v-progress-circular color="primary" indeterminate/>
            </v-layout>
            <v-data-table
                :headers="headers"
                :items="products"
                hide-actions
                :class="{'d-none': loading}"
            >
              <template v-slot:headers="{headers}">
                <tr>
                  <th v-for="header in headers" :key="header.value" :class="header.class">
                    <span class="header-cell">{{ header.text }}</span>
                    <v-tooltip top v-if="header.tooltip">
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on">help</v-icon>
                      </template>
                      <span>{{ header.tooltip }}</span>
                    </v-tooltip>
                  </th>
                </tr>
              </template>
              <template v-slot:items="{item}">
                <tr>
                  <td>
                    {{ item.name }}
                  </td>
                  <td>
                    {{ item.amount | format_number }} x
                  </td>
                  <td>
                    {{ item.weight.name }} <span v-if="item.weight.name !== 'gram'">({{ item.weight.weight | format_number }} g)</span>
                  </td>
                  <td>
                    {{ item.amount * item.weight.weight | round(2) | format_number }} g
                  </td>
                  <td>
                    {{ item.kcal | round(2) }}
                  </td>
                  <td>
                    {{ item.carbo | round(2) }}
                  </td>
                  <td>
                    {{ item.protein | round(2) }}
                  </td>
                  <td>
                    {{ item.fat | round(2) }}
                  </td>
                  <td>
                    {{ item.fiber | round(2) }}
                  </td>
                </tr>
              </template>
              <template v-slot:footer>
                <tr>
                  <td colspan="8"/>
                </tr>
                <tr v-if="portions > 1">
                  <td colspan="3">
                    <strong>
                      {{ trans('app.foods.total_n_portions', {portions: portions}) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalFoodWeight | round(2) }} g
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalFoodKcal | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalFoodCarbo | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalFoodProtein | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalFoodFat | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalFoodFiber | round(2) }}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td colspan="3">
                    <strong>
                      {{ trans('app.foods.total_n_portions', {portions: 1}) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalPortionWeight | round(2) }} g
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalPortionKcal | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalPortionCarbo | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalPortionProtein | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalPortionFat | round(2) }}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {{ totalPortionFiber | round(2) }}
                    </strong>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <div class="additional-info-container" :class="{'d-none': loading}" >
              <v-layout align-center class="pb-3">
                <v-flex xs3>
                  <strong>{{ `app.foods.additional_information`| trans }}:</strong>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on">help</v-icon>
                    </template>
                    <span>
                          {{ `app.foods.additional_information_description` | trans }}
                        </span>
                  </v-tooltip>
                </v-flex>
                <v-flex xs2>
                  <span>IG: {{ getFoodNutrientValue(GLICEMIC_INDEX()) | round(3) }}</span>
                </v-flex>
                <v-flex xs2>
                  <span>ŁG: {{ getFoodNutrientValue(GLICEMIC_LOAD()) | round(3) }}</span>
                </v-flex>
                <v-flex xs2>
                  <span>WW: {{ getFoodNutrientValue(WW()) | round(3) }}</span>
                </v-flex>
                <v-flex xs2>
                  <span>WBT: {{ getFoodNutrientValue(WBT()) | round(3) }}</span>
                </v-flex>
              </v-layout>
              <v-layout align-center>
                <v-flex xs6 class="align-self-start">
                  <strong>{{ `app.foods.recipe` | trans }}:</strong>
                  <p class="food-recipe">{{ recipe }}</p>
                </v-flex>
                <v-flex xs6>
                  <div class="tags-container">
                    <div>
                      <strong>{{ `app.tags.tags` | trans }}: </strong>
                      <span v-if="tags.length === 0">{{ 'app.no_data' | trans }}</span>
                      <v-chip color="green" text-color="white" v-for="tag in tags" :key="tag">{{ tag }}</v-chip>
                    </div>
                    <div>
                      <strong>{{ `app.foods.meals`|trans }}: </strong>
                      <span v-if="meals.length === 0">{{ 'app.no_data' | trans }}</span>
                      <v-chip color="green" text-color="white" v-for="meal in meals" :key="meal">{{ meal }}</v-chip>
                    </div>
                    <div>
                      <strong>{{ `app.foods.diet`|trans }}: </strong>
                      <span v-if="diets.length === 0">{{ 'app.no_data' | trans }}</span>
                      <v-chip color="green" text-color="white" v-for="diet in diets" :key="diet">{{ diet }}</v-chip>
                    </div>
                    <div>
                      <strong>{{ `app.foods.equipment`|trans }}: </strong>
                      <span v-if="equipments.length === 0">{{ 'app.no_data' | trans }}</span>
                      <v-chip color="green" text-color="white" v-for="equipment in equipments" :key="equipment">{{ equipment }}</v-chip>
                    </div>
                  </div>
                </v-flex>
              </v-layout>
            </div>
          </v-tab-item>
          <v-tab-item
              :key="`details`"
              :value="`details`">
            <v-layout align-center justify-center v-if="loading" class="loader-container">
              <v-progress-circular color="primary" indeterminate/>
            </v-layout>
            <nutrients-details ref="nutrientsDetails" :no-padding="true" :class="{'d-none': loading}" >
              <template #actions v-if="portions > 1">
                <v-flex xs12 sm5>
                    <v-switch color="primary"
                              v-model="showNutrientsForSinglePortion"
                              :label="trans('app.foods.show_nutrients_for_single_portion')">
                    </v-switch>
                </v-flex>
              </template>
            </nutrients-details>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import axios from "axios";
import {mapGetters} from "vuex";
import FoodInitialDataService from "../../services/FoodInitialDataService";
import {trans} from "../../app";
import {
  CARBO,
  FATS,
  FIBER,
  GLICEMIC_INDEX,
  GLICEMIC_LOAD,
  KCAL,
  PROTEIN, WBT,
  WW
} from "../../services/NutrientDetailsProvider";
import {round} from "../../utils/money";

export default {
  data: ()  => ({
      dialog: false,
      foodId: null,
      foodName: '',
      isSystem: false,
      isShared: false,
      canEdit: false,
      editUrl: '',
      selectedTab: 'basic',
      products: [],
      portions: null,
      foodNutrients: [],
      recipe: '',
      tags: [],
      meals: [],
      diets: [],
      equipments: [],
      showNutrientsForSinglePortion: false,
      foodInitialDataService: new FoodInitialDataService(),
      foodInitialData: null,
      loading: true,
  }),
  computed: {
    ...mapGetters(['hasPermission']),
    headers() {
      return [
        {
          text: trans('app.product'), align: 'left', value: 'name', sortable: false
        },
        {
          text: trans('app.foods.amount'), align: 'right', value: 'amount', sortable: false,
        },
        {
          text: trans('app.foods.unit'), align: 'left', sortable: false, tooltip: trans('app.foods.unit_tooltip')
        },
        {
          text: trans('app.foods.total'), align: 'left', sortable: false
        },
        {
          text: 'Kcal', align: 'left', value: 'kcal', sortable: false,
        },
        {
          text: 'W (g)', align: 'left', value: 'carbo', sortable: false,
        },
        {
          text: 'B (g)', align: 'left', value: 'protein', sortable: false,
        },
        {
          text: 'T (g)', align: 'left', value: 'fat', sortable: false,
        },
        {
          text: 'Bł (g)', align: 'left', value: 'fiber', sortable: false,
        },
      ];
    },
    totalFoodKcal() {
      return this.products.reduce((acc, product) => {
        return acc + product.kcal;
      }, 0);
    },
    totalPortionKcal() {
      return this.totalFoodKcal / this.portions;
    },
    totalFoodCarbo() {
      return this.products.reduce((acc, product) => {
        return acc + product.carbo;
      }, 0);
    },
    totalPortionCarbo() {
      return this.totalFoodCarbo / this.portions;
    },
    totalFoodProtein() {
      return this.products.reduce((acc, product) => {
        return acc + product.protein;
      }, 0);
    },
    totalPortionProtein() {
      return this.totalFoodProtein / this.portions;
    },
    totalFoodFat() {
      return this.products.reduce((acc, product) => {
        return acc + product.fat;
      }, 0);
    },
    totalPortionFat() {
      return this.totalFoodFat / this.portions;
    },
    totalFoodFiber() {
      return this.products.reduce((acc, product) => {
        return acc + product.fiber;
      }, 0);
    },
    totalPortionFiber() {
      return this.totalFoodFiber / this.portions;
    },
    totalFoodWeight() {
      return this.products.reduce((acc, product) => {
        return acc + (product.amount * product.weight.weight);
      }, 0);
    },
    totalPortionWeight() {
      return this.totalFoodWeight / this.portions;
    }
  },
  mounted() {
    this.foodInitialDataService.fetch().then(({data}) => {
      this.foodInitialData = data;
    }).catch((error) => {
      this.sentryCaptureException(error);
    })
  },
  methods: {
    WBT() {
      return WBT
    },
    WW() {
      return WW
    },
    GLICEMIC_LOAD() {
      return GLICEMIC_LOAD
    },
    GLICEMIC_INDEX() {
      return GLICEMIC_INDEX
    },
    open(id) {
      this.dialog = true;

      this.waitForNutrientsDetails().then(() => {
        this.loadFood(id);
      }).catch((error) => {
        this.sentryCaptureException(error);
      })
    },
    waitForNutrientsDetails() {
      return new Promise(resolve => {
        const interval = setInterval(() => {
          if (this.hasNutrientsDetails()
              && this.$refs.nutrientsDetails.isInitialized() === false
              && this.foodInitialData !== null) {
            clearInterval(interval);
            this.nutrientsDetailsInit();
            resolve();
          } else if (this.hasNutrientsDetails()
              && this.$refs.nutrientsDetails.isInitialized() === true) {
            clearInterval(interval)
            resolve();
          }
        }, 100);
      })
    },
    loadFood(id) {
      axios.get(`/v2/foods/${id}`).then((response) => {
        const food = response.data.data;
        this.foodId = id;
        this.foodName = food.name
        this.isSystem = food.is_system;
        this.isShared = food.shared;
        this.canEdit = food.can_edit;
        this.editUrl = food.edit_url;
        this.portions = food.portions;
        this.recipe = food.recipe;
        this.foodNutrients = food.nutrients;
        this.tags = food.tags.map((tag) => tag.name);
        this.meals = food.meals.map((meal) => meal.name);
        this.diets = food.diets.map((diet) => diet.name);
        this.equipments = food.equipments.map((equipment) => equipment.name);
        this.products = food.products.map((product) => {
          const portionFactor = product.amount * (product.weight.weight / 100)
          const kcal = product.nutrients.find(nutrient => nutrient.id === KCAL);
          const kcalValue = kcal ? kcal.value * portionFactor : 0;
          const carbo = product.nutrients.find(nutrient => nutrient.id === CARBO);
          const carboValue = carbo ? carbo.value * portionFactor : 0;
          const protein = product.nutrients.find(nutrient => nutrient.id === PROTEIN);
          const proteinValue = protein ? protein.value * portionFactor : 0;
          const fats = product.nutrients.find(nutrient => nutrient.id === FATS);
          const fatsValue = fats ? fats.value * portionFactor : 0;
          const fiber = product.nutrients.find(nutrient => nutrient.id === FIBER);
          const fiberValue = fiber ? fiber.value * portionFactor : 0;
          return {
            name: product.name,
            amount: product.amount,
            weight: product.weight,
            kcal: kcalValue,
            carbo: carboValue,
            protein: proteinValue,
            fat: fatsValue,
            fiber: fiberValue,
          }
        })
        this.nutrientsDetailsLoad(food.nutrients);
        this.loading = false;
      }).catch((error) => {
        this.sentryCaptureException(error);
      });
    },
    close() {
      this.foodId = null;
      this.foodName = '';
      this.isSystem = false;
      this.isShared = false;
      this.nutrientsDetailsUnload();
      this.showNutrientsForSinglePortion = false;
      this.dialog = false;
      this.selectedTab = 'basic';
      this.loading = true;
    },
    canBeEdited() {
      if (this.isSystem && this.hasPermission('food_system_edit')) {
        return true;
      }
      if (this.isShared && this.hasPermission('food_system_edit')) {
        return true;
      }
      return this.canEdit
    },
    getFoodNutrientValue(nutrientId) {
      const nutrient = this.foodNutrients.find(nutrient => nutrient.id === nutrientId);
      const nutrientValue = nutrient ? nutrient.value : 0;
      if (nutrientId !== GLICEMIC_INDEX) {
        return nutrientValue / this.portions
      }
      return nutrientValue;
    },
    edit() {
      this.$emit('edit', {id: this.foodId});
      this.close();
    },
    hasNutrientsDetails() {
      return this.$refs.nutrientsDetails !== undefined;
    },
    nutrientsDetailsIsInitialized() {
      return this.hasNutrientsDetails() && this.$refs.nutrientsDetails.isInitialized();
    },
    nutrientsDetailsInit() {
      return this.hasNutrientsDetails() && this.$refs.nutrientsDetails.init(this.foodInitialData);
    },
    nutrientsDetailsLoad(nutrients) {
      return this.hasNutrientsDetails() && this.$refs.nutrientsDetails.load(nutrients);
    },
    nutrientsDetailsUnload() {
      return this.hasNutrientsDetails() && this.$refs.nutrientsDetails.unload();
    }
  },
  watch: {
    showNutrientsForSinglePortion(showForSinglePortion) {
      this.nutrientsDetailsUnload();
      if (showForSinglePortion) {
        const singlePortionNutrients = this.foodNutrients.map((nutrient) => {
          const id = nutrient.id;
          if (id === GLICEMIC_INDEX) {
            return {
              id: id,
              value: nutrient.value
            }
          }
          return {
            id: id,
            value: round(nutrient.value / this.portions, 3).toFixed(3),
          }

        });
        this.nutrientsDetailsLoad(singlePortionNutrients);
        return;
      }
      this.nutrientsDetailsLoad(this.foodNutrients);
    }
  }
}
</script>
<style scoped>
.header-cell {
  font-weight: bold;
  color: #000000de;
}
.food-name-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.food-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.food-recipe {
  white-space: pre-wrap;
}
.tags-container {
  display: flex;
  flex-direction: column;
  padding-left: 30px;
}
.additional-info-container {
  font-size: 13px;
  padding-top: 40px;
}
>>> table.v-table thead tr {
  height: 30px;
}
>>> table.v-table tbody td {
  height: 30px;
}
>>> table.v-table tfoot tr {
  height: 30px;
  font-size: 13px;
}
>>> table.v-table tbody td {
  padding-left: 12px;
  padding-right: 12px;
}
>>> table.v-table tbody td:nth-child(2) {
  text-align: right;
  padding-right: 3px;
}
>>> table.v-table tbody td:nth-child(3) {
  padding-left: 3px;
}
>>> table.v-table thead th:nth-child(1) {
  width: 25%;
}
>>> table.v-table thead th {
  padding-left: 12px;
  padding-right: 12px;
}
>>> table.v-table thead th:nth-child(2) {
  text-align: right;
  padding-right: 3px;
}
>>> table.v-table thead th:nth-child(3) {
  padding-left: 3px;
}
>>> table.v-table tfoot tr:first-child {
  border-top: none;
}
>>> table.v-table tfoot tr:nth-child(2) {
  border-top: none;
}
>>> table.v-table tfoot td {
  padding-left: 12px;
  padding-right: 12px;
}
>>> .v-chip .v-chip__content {
  height: 25px;
}
.loader-container {
  height: 300px;
}
</style>
